import { UniversalSearch as Base } from '@fountain/universal-search';
import type {
  NavItem,
  PersonResult,
  RecordResult,
  SearchSection,
} from '@fountain/universal-search';
import queryString from 'query-string';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { APPLICANT_DRAWER_TABS } from 'containers/ApplicantDrawerPopup/constants';
import { ADMIN_ROLE } from 'containers/Auth_old/constants';
import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import type { WhoAmI } from 'containers/Auth_old/selectors';
import { POPUP_TYPES } from 'containers/GlobalPopup/constants';
import { usePostHog } from 'hooks/usePostHog';
import {
  REACT_APP_GLOBAL_API_BASE_URL_V2,
  REACT_APP_MONOLITH_BASE_URL,
} from 'runtimeEnvVars';
import { apiGet } from 'utils/axios';
import history from 'utils/history';

// Openings/Applicants access is data-scoped per user rather than an
// all-or-nothing page gate (FunnelPolicy#index scopes to accessible
// funnels), so they're always listed. Locations and Positions are hard
// page-level gates in the monolith (LocationPolicy#index?/PositionPolicy#index?:
// privileged? || manage_funnels? || create_funnels? || view_funnels?), and
// Workflows is gated by WorkflowPolicy#view_workflows? (privileged? ||
// workflows? || view_workflows?). Mirror those checks here so the palette
// doesn't surface links a user can't actually open.
function isPrivileged(whoami: WhoAmI | undefined): boolean {
  return Boolean(
    whoami?.is_owner || whoami?.is_super_admin || whoami?.role === ADMIN_ROLE,
  );
}

function canAccessFunnelSettings(whoami: WhoAmI | undefined): boolean {
  const perms = whoami?.perms ?? [];
  return (
    isPrivileged(whoami) ||
    perms.includes('manage_funnels') ||
    perms.includes('create_funnels') ||
    perms.includes('view_funnels')
  );
}

function canAccessWorkflows(whoami: WhoAmI | undefined): boolean {
  const perms = whoami?.perms ?? [];
  return (
    isPrivileged(whoami) ||
    perms.includes('workflows') ||
    perms.includes('view_workflows')
  );
}

// pathname is opaque to @fountain/universal-search — it's just handed back to
// onNavigate. Cross-app items carry a full absolute URL (whoami.wx_url /
// whoami.source_url) instead of an in-app path, and onNavigate tells them
// apart by checking for a URL scheme.
function isAbsoluteUrl(pathname: string): boolean {
  return /^https?:\/\//.test(pathname);
}

interface ApplicantSearchResult {
  id: string;
  name?: string;
  email?: string;
}

// A row of `api_self_serve/v2/openings` — the same payload that backs the
// Openings page table (Openings::Private::Mappers::OpeningsMapper).
interface OpeningSearchResult {
  funnel_name?: string;
  funnel_slug?: string;
  funnel_external_id?: string;
  account_slug?: string;
  brand_slug?: string | null;
  location_id?: string | null;
  location_name?: string | null;
  position_name?: string | null;
}

interface OpeningAccess {
  canViewApplicants: boolean;
  canManageFunnels: boolean;
}

// Where an opening row opens, mirroring the destination its title cell links to
// on the Openings page (containers/Openings/.../OpeningsTableCell): the
// applicant pipeline for anyone who can view applicants, the workflow editor
// for a funnel manager who can't, and the public preview otherwise. Returns a
// slug-relative path (onNavigate prepends the account slug) except for the
// preview, which is an absolute monolith URL. Openings whose payload is missing
// what every branch needs are dropped by the caller rather than linked to a
// half-built path.
function openingPathname(
  opening: OpeningSearchResult,
  { canViewApplicants, canManageFunnels }: OpeningAccess,
): string | undefined {
  if (
    canViewApplicants &&
    opening.brand_slug &&
    opening.location_id &&
    opening.funnel_external_id
  ) {
    return `/brands/${opening.brand_slug}/locations/${opening.location_id}/jobs/${opening.funnel_external_id}/v2/stages`;
  }
  if (!opening.funnel_slug) return undefined;
  if (canManageFunnels) return `/openings/${opening.funnel_slug}/workflow`;
  if (!opening.account_slug) return undefined;
  return `${REACT_APP_MONOLITH_BASE_URL}/${opening.account_slug}/apply/${opening.funnel_slug}?preview=true`;
}

function getAccountSlug(): string {
  return window.location.pathname.split('/').filter(Boolean)[0] ?? '';
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
}

export function HireUniversalSearch() {
  const posthog = usePostHog();
  const whoami = useSelector(makeSelectWhoami());
  const universalSearchEnabled = Boolean(
    whoami?.feature_flags?.['universal-search'],
  );

  // The exact two signals OpeningsTableCell reads before deciding where a
  // row's title links — `isAdminUser` (role === Admin, not the wider
  // owner/super-admin/Admin `isPrivileged` check used elsewhere in this file)
  // OR'd with the view_applicants perm, and manage_funnels read straight off
  // whoami.policies with no widening. Deliberately narrower than
  // isPrivileged()/canAccessFunnelSettings() above: those gate real Pundit
  // policies that include `privileged?` in their own Ruby definition, but
  // OpeningsTableCell's own predicate does not, so matching it here means not
  // widening either.
  const openingAccess = useMemo(() => {
    const perms = whoami?.perms ?? [];
    return {
      canViewApplicants:
        whoami?.role === ADMIN_ROLE || perms.includes('view_applicants'),
      canManageFunnels: Boolean(whoami?.policies?.manage_funnels),
    };
  }, [whoami]);

  // Cross-app items only appear when whoami says the account actually has
  // that product provisioned — same signal the top nav / Source link use
  // (whoami.wx_url and whoami.source_url are both nil server-side unless the
  // account is unified-auth'd onto that product). Openings/Applicants are
  // always listed since access to them is data-scoped rather than an
  // all-or-nothing gate; Workflows/Locations/Positions mirror the monolith's
  // own Pundit policies (see canAccessWorkflows/canAccessFunnelSettings).
  const navItems = useMemo((): NavItem[] => {
    const items: NavItem[] = [
      { label: 'Openings', pathname: '/openings', group: 'Core' },
      {
        label: 'All Applicants',
        pathname: '/applicants',
        group: 'Core',
        keywords: ['candidates', 'people'],
      },
    ];

    if (canAccessWorkflows(whoami)) {
      items.push({ label: 'Workflows', pathname: '/workflows', group: 'Core' });
    }

    if (canAccessFunnelSettings(whoami)) {
      items.push(
        { label: 'Locations', pathname: '/locations', group: 'Core' },
        { label: 'Positions', pathname: '/positions', group: 'Core' },
      );
    }

    // Account settings now live in WX for unified-auth accounts — only fall
    // back to Hire's own (legacy) settings page for accounts that aren't on
    // WX yet.
    items.push({
      label: 'Account Settings',
      pathname: whoami?.wx_url ? `${whoami.wx_url}/settings` : '/settings',
      group: 'Settings',
    });

    if (whoami?.fountain_ai_career_site_enabled) {
      items.push({
        label: 'Jobs Directory',
        pathname: '/ai_jobs_directory_career_site',
        group: 'Settings',
        keywords: ['career site', 'ai jobs directory'],
      });
    }

    if (whoami?.wx_url) {
      items.push({
        label: 'Worker Experience',
        pathname: whoami.wx_url,
        group: 'Worker Experience',
        keywords: ['wx', 'workers', 'pool', 'onboard', 'compliance'],
      });
    }

    if (whoami?.source_url) {
      items.push({
        label: 'Sourcing',
        pathname: whoami.source_url,
        group: 'Worker Experience',
        keywords: ['source', 'sourcing dashboard'],
      });
    }

    return items;
  }, [whoami]);

  // Searches applicants account-wide via the monolith self-serve API. Mirrors
  // the Master Applicants View fetch in hooks/useApplicants.js — same `query`
  // free-text param and the same cookie-based auth (apiGet sends credentials);
  // the current account is inferred from the session. Returns slug-relative
  // pathnames — onNavigate prepends the account slug.
  const searchPeople = useCallback(
    async (query: string): Promise<PersonResult[]> => {
      const trimmed = query.trim();
      if (trimmed.length < 2) return [];

      const url = `${REACT_APP_GLOBAL_API_BASE_URL_V2}/applicants?${queryString.stringify(
        {
          query: trimmed,
          page: 1,
          per_page: 8,
          c: ['name', 'email'],
        },
        { arrayFormat: 'bracket' },
      )}`;

      try {
        const { data } = await apiGet(url);
        const applicants: ApplicantSearchResult[] = data?.applicants ?? [];
        return applicants
          .filter(applicant => Boolean(applicant.id))
          .map(applicant => ({
            id: applicant.id,
            label: applicant.name || applicant.email || applicant.id,
            subtitle: applicant.email,
            initials: applicant.name ? getInitials(applicant.name) : undefined,
            pathname: `/applicants?${queryString.stringify({
              applicantId: applicant.id,
              applicantOpenSource: 'universal_search',
              applicant_table_selection: false,
              page: 1,
              per_page: 20,
              persistedPopupType: POPUP_TYPES.APPLICANT_DRAWER_POPUP,
              tabIndex: APPLICANT_DRAWER_TABS.GENERAL,
            })}`,
          }));
      } catch {
        return [];
      }
    },
    [],
  );

  // Searches openings account-wide via the same endpoint (and the same free-text
  // `query` param) the Openings page search box uses, so the palette matches on
  // exactly what that page matches on — title, location name/address, location
  // group, position and brand (Openings::…::FunnelsProvider#search_funnels).
  // Results are already scoped to the current user's accessible funnels
  // server-side, which is why there's no page-level gate on this section.
  const searchOpenings = useCallback(
    async (query: string): Promise<RecordResult[]> => {
      const trimmed = query.trim();
      if (trimmed.length < 2) return [];

      // The table's own "active only" default is account-configurable
      // (whoami.hide_inactive_openings), not a fixed client default, so this
      // isn't literal parity with it — it's a deliberate choice for the
      // palette specifically: at a 5-row cap, a closed/archived opening
      // outranking one still open to hire against is a worse result almost
      // always, regardless of what the account's table happens to default to.
      //
      // `status_filters` rides alongside the legacy `active` param rather than
      // replacing it. The backend only honors `status_filters` for accounts
      // with the status-v2 table enabled — everyone else falls back to the
      // plain `active` filter, unchanged. On a status-v2 account, sending the
      // correlated group also excludes unlisted openings from this "active"
      // result set, matching the table's own default active view
      // (Openings::Private::Providers::FunnelsProvider#apply_status_filter).
      const url = `${REACT_APP_GLOBAL_API_BASE_URL_V2}/openings?${queryString.stringify(
        {
          query: trimmed,
          page: 1,
          per_page: 5,
          active: true,
          status_filters: JSON.stringify([{ active: true }]),
        },
      )}`;

      try {
        const { data } = await apiGet(url);
        const openings: OpeningSearchResult[] = data?.openings ?? [];
        const results: RecordResult[] = [];
        openings.forEach(opening => {
          // A malformed entry (not an object — e.g. `null`) must be skipped on
          // its own, not thrown: `openingPathname` reads properties straight
          // off `opening`, and an uncaught throw here would abort the whole
          // forEach and drop every other, well-formed opening in the same
          // response via the outer catch below.
          if (!opening || typeof opening !== 'object') return;
          const pathname = openingPathname(opening, openingAccess);
          if (!pathname || !opening.funnel_external_id) return;
          results.push({
            id: opening.funnel_external_id,
            label:
              opening.funnel_name ||
              opening.funnel_slug ||
              opening.funnel_external_id,
            subtitle:
              [opening.location_name, opening.position_name]
                .filter(Boolean)
                .join(' · ') || undefined,
            pathname,
          });
        });
        return results;
      } catch {
        return [];
      }
    },
    [openingAccess],
  );

  const searchSections = useMemo(
    (): SearchSection[] => [
      { key: 'openings', label: 'Openings', search: searchOpenings },
    ],
    [searchOpenings],
  );

  const onNavigate = useCallback((pathname: string) => {
    if (isAbsoluteUrl(pathname)) {
      window.location.href = pathname;
      return;
    }
    const slug = getAccountSlug();
    history.push(slug ? `/${slug}${pathname}` : pathname);
  }, []);

  const onAnalytics = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      posthog.capture(event as any, properties ?? {});
    },
    [posthog],
  );

  // Dark-launched behind the `universal-search` LaunchDarkly flag — mirrors
  // WX's `universalSearch` gate for this same feature.
  if (!universalSearchEnabled) return null;

  return (
    <Base
      searchPeople={searchPeople}
      searchSections={searchSections}
      navItems={navItems}
      onNavigate={onNavigate}
      onAnalytics={onAnalytics}
      placeholder="Search applicants, openings and settings…"
      peopleSectionLabel="Applicants"
    />
  );
}
